import * as React from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../core/i18n'
import { thanksMailService, userService } from '../../../core/services'
import SvgIcon from 'components/atoms/SvgIcon'
import {
  COLORS,
  BREAKPOINT_TABLET_SMALL,
  BREAKPOINT_TABLET_MOBILE,
} from '../../../static/constants'
import { Button, Select, DropZoneUploader, Panel } from '../../atoms'
import { Form } from '../../molecules'
import * as interfaces from '../../../static/interfaces'
import SidebarLayout from '../_layouts/sidebarLayout'

declare var window: interfaces.IWindow

interface IThanksMailTemplate {
  name: string
  title: string
  text1: string
  text2: string
  text3: string
  guest_emails: string[]
  shareable_review_url?: string
}
interface IProps {
  contents: IThanksMailTemplate[]
  isDirectReservation: boolean
  reservation: {
    id: number
    guest_email?: string
  }
  shareable_review_url: string
  user: any
}

interface IPreviewImage {
  file: any
  url: string
}

interface IErrors {
  [key: string]: string | null
}

const FIELDS = {
  title: 'title',
  text1: 'text1',
  text3: 'text3',
}
const getFormattedTemplateOptions = (templetes: IThanksMailTemplate[]) => {
  return templetes.map((template, index) => ({ value: `${index}`, label: template.name }))
}
const ThanksEmailsEdit: React.FC<IProps> = props => {
  const fields = React.useMemo(() => {
    return props.isDirectReservation ? { ...FIELDS, guest_emails: 'guest_emails' } : FIELDS
  }, [props])
  const initTemplate: IThanksMailTemplate = props.contents[0] ?? {
    name: '',
    title: '',
    text1: '',
    text2: '',
    text3: '',
    guest_emails: [],
  }

  const [sending, setSending] = React.useState<boolean>(false)
  const [inputTitle, setInputTitle] = React.useState<string>(initTemplate.title)
  const [inputText1, setInputText1] = React.useState<string>(initTemplate.text1)
  const [inputText2, setInputText2] = React.useState<string>(initTemplate.text2)
  const [inputText3, setInputText3] = React.useState<string>(initTemplate.text3)
  const [previewImages, setPreviewImages] = React.useState<IPreviewImage[]>([])
  const [errors, setErrors] = React.useState<IErrors>({})
  const { user } = userService.getUserFromJson(props.user)

  const handleSubmit = React.useCallback(
    async (initialValues, values) => {
      const formData = new FormData()
      const targetReservationId = props.isDirectReservation
        ? 'direct_reservation_id'
        : 'reservation_id'
      formData.append('title', inputTitle)
      formData.append('text1', inputText1)
      formData.append('text2', inputText2)
      formData.append('text3', inputText3)
      formData.append('guest_emails', values.guest_emails)
      formData.append('shareable_review_url', props.shareable_review_url)
      formData.append(targetReservationId, String(props.reservation.id))

      imagesToUpload.current.forEach((image, index) => {
        formData.append(`mail_image[images][]`, image.file)
      })
      setSending(true)
      try {
        await thanksMailService.postThanksMail(props.reservation.id, formData)
        window.flashMessages.addMessage({ text: I18n.t('generic.send_success') })
        setTimeout(() => {
          location.href = '/agent/host_reservations?past=true'
        }, 1000)
      } catch (e) {
        setSending(false)
      }
    },
    [inputTitle, inputText1, inputText2, inputText3]
  )
  const onChangeTemplate = event => {
    const selectedTemplate = props.contents[event.target.value]
    setInputTitle(selectedTemplate.title)
    setInputText1(selectedTemplate.text1)
    setInputText2(selectedTemplate.text2)
    setInputText3(selectedTemplate.text3)
  }

  const imagesToUpload = React.useRef(previewImages)
  React.useEffect(() => {
    imagesToUpload.current = previewImages
  }, [previewImages])

  const onDropHandler = addedImages => {
    addedImages.forEach(addedImage => {
      const fileReader = new FileReader()

      fileReader.onloadend = (event: any) => {
        const loadedImage = {
          file: addedImage,
          url: event.target.result,
        }
        setPreviewImages(currentPreviewImages => [...currentPreviewImages, loadedImage])
      }
      fileReader.readAsDataURL(addedImage)
    })
  }

  const deletePreviewImage = deleteIndex => {
    setPreviewImages(currentPreviewImages =>
      currentPreviewImages.filter((image, index) => index !== deleteIndex)
    )
  }
  const OPTIONS = props.contents.length > 1 ? getFormattedTemplateOptions(props.contents) : []
  return (
    <SidebarLayout user={user}>
      <Panel>
        <S.ThanksMailEdit>
          <h1>{I18n.t('mail_template.thanks_mail')}</h1>
          {props.contents.length > 1 && (
            <SelectWrapper>
              <p className="Input_Title">Select Template</p>
              <Select
                name="name"
                options={OPTIONS}
                defaultValue={OPTIONS[0].value}
                onChangeHandler={onChangeTemplate}
              />
            </SelectWrapper>
          )}
          <Form fields={fields} handleSubmit={handleSubmit}>
            {props.isDirectReservation && (
              <S.FormItemWrapper>
                <InputWrapper>
                  <p className="Input_Title Input_Email_Title">To:</p>
                  <div className="Input_Email">
                    <input
                      id="guest_emails"
                      type="text"
                      name="guest_emails"
                      required={true}
                      defaultValue={props.reservation.guest_email}
                    />
                    <EmailAnnotation>※カンマ区切りで複数設定可能です。</EmailAnnotation>
                    {errors.guest_emails && (
                      <span className="InputText_Error">{errors.guest_emails}</span>
                    )}
                  </div>
                </InputWrapper>
              </S.FormItemWrapper>
            )}
            <S.Main>
              <S.InputArea>
                <S.FormItemWrapper>
                  <InputWrapper>
                    <p className="Input_Title">Title</p>
                    <div className="Input_Main">
                      <input
                        id="title"
                        type="text"
                        name="title"
                        required={true}
                        value={inputTitle}
                        onChange={event => setInputTitle(event.target.value)}
                      />
                      {errors.title && <span className="InputText_Error">{errors.title}</span>}
                    </div>
                  </InputWrapper>
                </S.FormItemWrapper>
                <S.FormItemWrapper>
                  <InputWrapper>
                    <p className="Input_Title">Text1</p>
                    <div className="Input_Main">
                      <textarea
                        id="text1"
                        name="text1"
                        required={true}
                        value={inputText1}
                        onChange={event => setInputText1(event.target.value)}
                      />
                      {errors.title && <span className="InputText_Error">{errors.title}</span>}
                    </div>
                  </InputWrapper>
                </S.FormItemWrapper>
                <S.FormItemWrapper>
                  <InputWrapper>
                    <p className="Input_Title">Text2</p>
                    <div className="Input_Main">
                      <textarea
                        id="text2"
                        name="text2"
                        required={true}
                        value={inputText2}
                        readOnly={true}
                      />
                      {errors.title && <span className="InputText_Error">{errors.title}</span>}
                    </div>
                  </InputWrapper>
                  <p className="note">※Uneditable</p>
                </S.FormItemWrapper>
                <S.FormItemWrapper>
                  <InputWrapper>
                    <p className="Input_Title">Text3</p>
                    <div className="Input_Main">
                      <textarea
                        id="text3"
                        name="text3"
                        required={true}
                        value={inputText3}
                        onChange={event => setInputText3(event.target.value)}
                      />
                      {errors.title && <span className="InputText_Error">{errors.title}</span>}
                    </div>
                  </InputWrapper>
                </S.FormItemWrapper>
              </S.InputArea>
              <S.PreviewArea>
                <S.PreviewTitle>preview</S.PreviewTitle>
                <S.PreviewMain>
                  <div>{inputText1}</div>
                  <div>{inputText2}</div>
                  <div>{inputText3}</div>
                </S.PreviewMain>
              </S.PreviewArea>
            </S.Main>
            {props.shareable_review_url && props.shareable_review_url.indexOf('token') >= 0 && (
              <div>
                <h4>{I18n.t('generic.link_explanation')}</h4>
                <p>
                  <a href={`${props.shareable_review_url}`}>{props.shareable_review_url}</a>
                  <br />
                  {I18n.t('generic.link_validation')}
                </p>
                <input
                  type="hidden"
                  name="shareable_review_url"
                  value={`${props.shareable_review_url}`}
                />
              </div>
            )}
            <S.ImageArea>
              <p className="Input_Title">Images</p>
              <S.ImagePreviewArea>
                {previewImages.map((image, index) => (
                  <S.ImageUpload_Cell key={index}>
                    <S.ImageUpload_Image>
                      <img src={image.url} alt="" />
                    </S.ImageUpload_Image>
                    <Button handleClick={() => deletePreviewImage(index)} small={true}>
                      {I18n.t('generic.delete')}
                    </Button>
                  </S.ImageUpload_Cell>
                ))}
                <DropZoneUploader onDrop={onDropHandler} />
              </S.ImagePreviewArea>
            </S.ImageArea>
            <S.Buttons>
              <div>
                <Button>
                  <a href="/agent/host_reservations?past=true">
                    <SvgIcon
                      name="keyboard_arrow_left"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    {I18n.t('generic.back_to_index')}
                  </a>
                </Button>
              </div>
              <div>
                <Button primary={true} disabled={sending}>
                  {I18n.t(sending ? 'generic.sending' : 'generic.send')}
                </Button>
              </div>
            </S.Buttons>
          </Form>
        </S.ThanksMailEdit>
      </Panel>
    </SidebarLayout>
  )
}
const S: { [key: string]: AnyStyledComponent } = {}

S.ThanksMailEdit = styled.div`
  padding-top: 12px;
  background-color: ${COLORS.white};
  max-width: 880px;
  margin: 0 auto;

  h1 {
    font-size: 18px;
    font-size: 18px;
    margin: 12px 10px;
  }
  .Input_Title {
    min-width: 70px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    margin-bottom: 18px;
  }
  .Input_Email_Title {
    margin-top: 10px;
    margin-left: 10px;
  }
`
S.Main = styled.div`
  display: flex;
  width: 100%;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
`
S.InputArea = styled.div`
  width: 50%;
  margin: 10px;

  .InputText_Required {
    display: none;
  }
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    width: auto;
    border-bottom: double 3px ${COLORS.divider};
    padding-bottom: 30px;
  }
`
S.FormItemWrapper = styled.div`
  & + & {
    margin-top: 24px;
  }
  .note {
    margin-left: 70px;
    font-size: 14px;
    margin-top: 5px;
    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      margin-left: 0px;
    }
  }
`
S.PreviewArea = styled.div`
  width: 50%;
  margin: 36px 10px 10px 10px;

  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    width: auto;
    margin: 10px;
  }
`
S.PreviewTitle = styled.div`
  font-size: 15px;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 18px;
`
S.PreviewMain = styled.div`
  border: solid 1px ${COLORS.divider};
  border-radius: 4px;
  height: 94%;
  padding: 10px;
  word-break: break-word;
  white-space: pre-wrap;

  div + div {
    margin-top: 20px;
  }
`
S.Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 10px;
`

S.ImageArea = styled.div`
  border-top: solid 1px ${COLORS.divider};
  margin-top: 24px;
  display: flex;
  width: 100%;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
    width: auto;
    margin: 10px;
  }

  .Input_Title {
    min-width: 70px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    margin-top: 24px;
    margin-bottom: 0px;
  }
`

S.ImagePreviewArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
  width: 100%;

  .DropZoneUploader {
    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      width: calc(50% - 32px);
    }

    @media (max-width: ${BREAKPOINT_TABLET_MOBILE}px) {
      width: calc(100% - 32px);
    }
  }
`
S.ImageUpload_Cell = styled.div`
  width: calc(33.33% - 32px);
  margin: 24px 16px 0;

  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    width: calc(50% - 32px);
  }

  @media (max-width: ${BREAKPOINT_TABLET_MOBILE}px) {
    width: calc(100% - 32px);
  }
`
S.ImageUpload_Image = styled.div`
  position: relative;
  width: 100%;
  height: 240px;
  border-radius: 4px;
  background-color: ${COLORS.white};
  overflow: hidden;
  margin-bottom: 8px;

  > img {
    width: 100%;
    height: inherit;
    object-fit: contain;
  }
`
const SelectWrapper = styled.div`
  margin: 16px 10px;
  padding-bottom: 16px;
  border-bottom: solid 1px ${COLORS.divider};
`
const InputWrapper = styled.div`
  display: flex;
  width: 100%;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }

  textarea {
    display: block;
    width: 100%;
    height: 160px;
    padding: 8px 12px;
    border: solid 1px ${COLORS.divider};
    border-radius: 4px;
    font-size: 15px;
    transition: border 0.2s ease;
    outline: none;
    background: none;
    line-height: 1.5;

    &:focus {
      border: solid 1px ${COLORS.primary};
    }
  }

  input {
    display: block;
    width: 100%;
    padding: 8px 12px;
    border: solid 1px ${COLORS.divider};
    border-radius: 4px;
    font-size: 15px;
    transition: border 0.2s ease;
    outline: none;
    background: none;

    &:not([readonly]):focus {
      border: solid 1px ${COLORS.primary};
    }
  }
  .Input_Main {
    flex: 1;
  }
  .Input_Email {
    width: 100%;
    flex: 1;
    margin-right: 10px;
  }
  .InputText_Error {
    display: block;
    margin-top: 8px;
    color: ${COLORS.cautionText};
    font-size: 14px;
  }
`
const EmailAnnotation = styled.div`
  color: gray;
  font-size: 12px;
`
export default ThanksEmailsEdit
